import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText
} from '@mui/material';
import CustomPagination from 'components/pagination';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';
import { openStudentInfoInNewTab } from 'utils/studentInfoUtils';

const columns = [
  { id: 'studentNumber', label: '학번', minWidth: 100, align: 'center' },
  { id: 'studentName', label: '이름', width: 70, align: 'center' },
  { id: 'departmentName', label: '학과', minWidth: 100, align: 'center' },
  { id: 'issuedNumber', label: '발급번호', minWidth: 100, align: 'center' }, // scholarshipNumber → issuedNumber
  { id: 'issuedYear', label: '년도', width: 65, align: 'center' },
  { id: 'issuedSemester', label: '학기', width: 50, align: 'center' },
  { id: 'level', label: '과정', width: 70, align: 'center' },
  { id: 'moduleName', label: '모듈명', width: 80, align: 'center' },
  { id: 'amount', label: '장학금액', width: 100, align: 'center' },
  { id: 'totalGrade', label: '총성적', width: 80, align: 'center' },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' }
];

const scholarshipAmounts = [
  { level: 'SS', amount: 1000000 },
  { level: 'S', amount: 600000 },
  { level: 'A', amount: 500000 },
  { level: 'ZERO', amount: 0 }
];

export default function AdminScholarshipAdd() {
  const rowsPerPage = 20;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);

  const [open, setOpen] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);

  const [level, setLevel] = useState('');
  const [studentNumberInfo, setStudentNumberInfo] = useState(null);

  const [sortConfig, setSortConfig] = useState({ field: '', direction: '' });

  const [moduleFilterSelected, setModuleFilterSelected] = useState('');
  const [moduleFilter, setModuleFilter] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  // 정렬기능: 선택된 컬럼에 대해 정렬순서 토글
  const handleSort = () => {
    const isDesc = sortConfig.field === 'totalGrade' && sortConfig.direction === 'desc';
    setSortConfig({ field: 'totalGrade', direction: isDesc ? 'asc' : 'desc' });
  };

  const handleModuleChange = (event) => setModuleFilterSelected(event.target.value);

  const fetchData = async () => {
    try {
      let url = `/api/admin/scholarships/pending?page=${page}&size=${rowsPerPage}`; // pending 추가

      // 모듈 필터 정보를 url에 추가
      if (moduleFilterSelected) {
        url += `&moduleName=${moduleFilterSelected}`;
      }

      // 정렬정보를 url에 추가
      if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
      }

      const response = await http.getWithToken(url);

      // 응답 구조 변경: response.data.data → response.data.result
      setRows(response.data.result.content);
      setTotalPages(response.data.result.totalPages);
    } catch (error) {
      alert('데이터 불러오는데 실패하였습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleChangePage = (event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage - 1);
    }
  };

  // 모듈 필터링 목록 가져오기
  useEffect(() => {
    const fetchModules = async () => {
      try {
        // const response = await http.getWithToken('/api/admin/scholarships/filter');
        // if (response.status === 200 && response.data.data.length > 0) {
        //   const filters = response.data.data[0];
        //   setModuleFilter(filters.moduleFilter);
        // }
        setModuleFilter([
          { id: '이공계', moduleName: '이공계' },
          { id: '비이공계', moduleName: '비이공계' },
          { id: '로봇기구', moduleName: '로봇기구' },
          { id: '로봇전장', moduleName: '로봇전장' },
          { id: '로봇제어', moduleName: '로봇제어' },
          { id: '로봇지능', moduleName: '로봇지능' },
          { id: '융합-로봇마케팅', moduleName: '융합-로봇마케팅' },
          { id: '융합-로봇미디어커뮤니케이션', moduleName: '융합-로봇미디어커뮤니케이션' },
          { id: '내비게이션', moduleName: '내비게이션' },
          { id: '로봇SI', moduleName: '로봇SI' },
          { id: '매니퓰레이션', moduleName: '매니퓰레이션' },
          { id: '인간로봇상호작용', moduleName: '인간로봇상호작용' },
          { id: 'AI시스템', moduleName: 'AI시스템' },
          { id: 'HW시스템', moduleName: 'HW시스템' }
        ]);
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      }
    };

    fetchModules();
  }, []);

  // 장학증서 리스트 불러오기
  useEffect(() => {
    fetchData();
  }, [page, moduleFilterSelected, sortConfig]); // moduleFilter 제거

  //  최종승인
  const handleConfirm = async () => {
    try {
      // 현재 페이지의 모든 장학금 ID 수집
      const scholarshipIds = rows.map(row => row.id);

      // 요청 바디에 ID 배열 포함
      const requestBody = {
        ids: scholarshipIds
      };

      const response = await http.postWithToken('/api/admin/scholarships/confirm', requestBody);

      if (response.data.status === 'SUCCESS') {
        alert('최종 승인에 성공하였습니다.');
        handleFinalClose();
        navigate('/admin-scholarship-certificate-management');
      } else {
        alert('오류가 발생했습니다. 다시 시도해주세요');
        handleFinalClose();
      }
    } catch (error) {
      alert('오류가 발생했습니다. 다시 시도해주세요');
      handleFinalClose();
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await http.getWithToken('/api/admin/scholarships/pending/excel', {}, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', 'scholarships.xlsx');
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (err) {
      alert('다운로드 중 오류가 발생했습니다. 잠시 후에 다시 시도해주세요');
    }
  };

  const handleClickOpen = async (scholarshipId) => {
    setStudentNumberInfo(scholarshipId); // studentNumber 대신 scholarship ID 저장
    setOpen(true);
  };

  const handleClose = async () => {
    setStudentNumberInfo(null);
    setLevel('');
    setOpen(false);
  };

  const handleFinalOpen = async () => {
    setFinalOpen(true);
  };

  const handleFinalClose = async () => {
    setFinalOpen(false);
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    setLevel(value);
  };

  // 장학금 등급 설정 함수 수정
  const handleAmountSetting = async () => {
    if (!level) {
      alert('등급을 선택해 주세요.');
      return;
    }

    // 요청 바디 단순화: amount만 전송
    const amountToSubmit = {
      amount: level // S, A, ZERO 등
    };

    try {
      // API 경로에 scholarship ID 사용 (studentNumber가 아닌 row.id)
      const response = await http.postWithToken(`/api/admin/scholarships/${studentNumberInfo}/amount`, amountToSubmit);

      if (response.data.status === 'SUCCESS') {
        alert('장학금 금액 산정을 성공하였습니다');
        fetchData();
        handleClose();
      } else {
        alert('오류가 발생했습니다. 다시 시도해주세요');
      }
    } catch (error) {
      alert(error.response?.data?.error || '오류가 발생했습니다.');
    }
  };

  // 신청 취소 함수 추가
  const handleCancelScholarship = async (scholarshipId) => {
    try {
      const response = await http.postWithToken(`/api/admin/scholarships/${scholarshipId}/cancel`);

      if (response.data.status === 'SUCCESS') {
        alert('장학금 신청이 취소되었습니다.');
        fetchData(); // 목록 새로고침
      } else {
        alert('취소 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      alert('취소 처리 중 오류가 발생했습니다.');
    }
  };

  // 취소 확인 팝업 상태 추가
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [scholarshipToCancel, setScholarshipToCancel] = useState(null);

  // 취소 확인 팝업 열기
  const handleCancelClick = (scholarshipId) => {
    setScholarshipToCancel(scholarshipId);
    setCancelDialogOpen(true);
  };

  // 취소 확인 팝업 닫기
  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setScholarshipToCancel(null);
  };

  return (
    <>
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh">
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <Box
            sx={{
              display: 'flex',
              width: '45%',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <Box sx={{ width: '12%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="module-filter-label">
                  모듈
                </InputLabel>
                <Select value={moduleFilterSelected} label="Module" onChange={handleModuleChange}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  {moduleFilter.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.moduleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', width: '30%' }}>
              <Button onClick={handleExportToExcel} variant="contained">
                엑셀추출
              </Button>
            </Box>
            <Button variant="contained" color="primary" onClick={handleFinalOpen}>
              최종승인
            </Button>
          </Box>
        </Box>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 1200, overflowX: 'hidden' }}>
            <Table sx={{ fontSize: '0.8rem', tableLayout: 'fixed', width: '100%' }} stickyHeader aria-label="sticky table">
              <TableHead sx={{ backgroundColor: '#bfbfbf' }}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        cursor: column.id === 'totalGrade' ? 'pointer' : 'default',
                        fontSize: '0.8rem',
                        ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width })
                      }}
                      onClick={column.id === 'totalGrade' ? handleSort : undefined}
                    >
                      {column.label}
                      {column.id === 'totalGrade' && sortConfig.field === 'totalGrade' && (sortConfig.direction === 'desc' ? ' 🔽' : ' 🔼')}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                      현재 목록이 비어있습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={`${row.studentNumber}-${row.moduleName}`}>
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === 'action') {
                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleClickOpen(row.id)} // row.id 사용
                                    sx={{
                                      minWidth: 'auto',
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    등급지정
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleCancelClick(row.id)} // row.id 사용
                                    sx={{
                                      minWidth: 'auto',
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    신청취소
                                  </Button>
                                </Box>
                              </TableCell>
                            );
                          } else if (column.id === 'amount') {
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.8rem' }}>
                                {value !== null ? `${value.toLocaleString()} 원` : ''}
                              </TableCell>
                            );
                          } else if (column.id === 'level') { // process → level으로 변경
                            let course;
                            if (value === '초급') {
                              course = '초급';
                            } else if (value === '중급') {
                              course = '중급';
                            } else if (value === '고급') {
                              course = '고급';
                            } else {
                              course = value;
                            }
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{ fontSize: '0.8rem', ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width }) }}
                              >
                                {course}
                              </TableCell>
                            );
                          } else if (column.id === 'studentNumber' || column.id === 'studentName') {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.8rem',
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => openStudentInfoInNewTab(row.studentNumber)}
                              >
                                {column.format && typeof value === 'number' ? column.format(value) : value}
                              </TableCell>
                            );
                          } else {
                            const isLongText = ['departmentName', 'issuedNumber', 'moduleName'].includes(column.id);
                            const displayValue = column.format && typeof value === 'number' ? column.format(value) : value;
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.8rem',
                                  ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width }),
                                  ...(isLongText && {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 0,
                                    whiteSpace: 'nowrap'
                                  })
                                }}
                                title={isLongText ? displayValue : undefined}
                              >
                                {displayValue}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <CustomPagination count={totalPages} page={page} onChange={handleChangePage} />
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>장학금 등급 지정</DialogTitle>
        <DialogContent>
          <DialogContentText>장학금 등급을 지정할 수 있습니다</DialogContentText>
          <Box
            noValidate
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginX: '96px'
            }}
          >
            <FormControl fullWidth style={{ marginTop: 16 }}>
              <InputLabel sx={{ paddingY: 0.3 }} id="course-select-label">
                등급 선택
              </InputLabel>
              <Select labelId="course-select-label" id="course-select" value={level} label="과정" onChange={handleAmountChange}>
                {scholarshipAmounts.map((scholarship, index) => (
                  <MenuItem key={index} value={scholarship.level}>
                    {`${scholarship.level} 등급 `}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {level ? (
              <p>
                <Typography component="span">선택하신 장학금 등급의 금액은 </Typography>
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  {scholarshipAmounts.find((item) => item.level === level).amount.toLocaleString()}
                </Typography>
                <Typography component="span"> 원 입니다.</Typography>
              </p>
            ) : (
              <p></p>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAmountSetting}>지정</Button>
          <Button onClick={handleClose}>취소</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={finalOpen} onClose={handleFinalClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'현재 페이지 장학금 목록 최종 승인'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', margin: '5px', mt: '20px' }}>
            현재 페이지의 장학금 지정 목록을 최종 승인하시겠습니까?
          </DialogContentText>
          <DialogContentText sx={{ color: '#d32f2f', fontSize: '0.8rem', margin: '5px' }}>
            등급을 지정하지 않은 신청자들은 ZERO 등급으로 처리 됩니다. <br />
            다른 페이지의 목록을 최종 승인하려면 다른 페이지로 이동하여 최종 승인을 진행해주세요.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} autoFocus>
            예
          </Button>
          <Button onClick={handleFinalClose}>아니오</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          장학금 신청 취소 확인
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            정말로 이 장학금 신청을 취소하시겠습니까?
          </DialogContentText>
          <DialogContentText sx={{ color: '#d32f2f', fontSize: '0.9rem', marginTop: '10px' }}>
            취소된 신청은 복구할 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            아니오
          </Button>
          <Button
            onClick={() => {
              handleCancelScholarship(scholarshipToCancel);
              handleCancelDialogClose();
            }}
            color="error"
            autoFocus
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
